import axios from 'axios';
import { API_BASE_URL } from '../config/env';
import { trackRequestEnd, trackRequestStart } from './globalLoading';
import { notifyUnauthorized } from './sessionAuth';

export const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Collapse duplicate GETs (StrictMode remount / overlapping hooks).
 * Always attach the *current* request config onto the resolved response so
 * global loader start/end stay paired (shared cache must not reuse another
 * request's config._globalLoadingTracked flag).
 */
const inflightGets = new Map();
const recentGets = new Map();
const RECENT_GET_TTL_MS = 800;

function buildGetKey(config) {
  const method = String(config.method || 'get').toLowerCase();
  if (method !== 'get') return null;
  if (config.skipDedupe) return null;
  try {
    return `get:${axiosClient.getUri(config)}`;
  } catch {
    const url = String(config.url || '');
    const params = config.params ? JSON.stringify(config.params) : '';
    return `get:${url}?${params}`;
  }
}

function asResponseForConfig(response, config) {
  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    config,
    request: response.request,
  };
}

function storeRecent(key, response) {
  recentGets.set(key, {
    expiresAt: Date.now() + RECENT_GET_TTL_MS,
    // Store body only — never another caller's config/tracking flags.
    snapshot: {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      request: response.request,
    },
  });
}

const defaultAdapter = axios.getAdapter(axiosClient.defaults.adapter);
axiosClient.defaults.adapter = (config) => {
  const key = buildGetKey(config);
  if (!key) return defaultAdapter(config);

  const cached = recentGets.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(asResponseForConfig(cached.snapshot, config));
  }

  const existing = inflightGets.get(key);
  if (existing) {
    return existing.then((response) => asResponseForConfig(response, config));
  }

  const pending = Promise.resolve(defaultAdapter(config))
    .then((response) => {
      storeRecent(key, response);
      return response;
    })
    .finally(() => {
      inflightGets.delete(key);
    });

  inflightGets.set(key, pending);
  // Leader keeps its own config on the raw adapter response.
  return pending.then((response) => asResponseForConfig(response, config));
};

axiosClient.interceptors.request.use(
  (config) => {
    trackRequestStart(config);

    if (config.skipAuth) {
      return config;
    }
    try {
      const token = window.localStorage.getItem('auth-token');
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      }
    } catch {
      // Ignore storage access issues in restricted environments.
    }
    return config;
  },
  (error) => {
    trackRequestEnd(error?.config);
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => {
    trackRequestEnd(response.config);
    return response;
  },
  (error) => {
    trackRequestEnd(error?.config);
    const status = error?.response?.status;
    const url = String(error?.config?.url || '');
    const isLoginAttempt = url.includes('/sign-in');
    if (status === 401 && !error?.config?.skipAuth && !isLoginAttempt) {
      notifyUnauthorized();
    }
    return Promise.reject(error);
  }
);
